import { Modal } from 'react-bootstrap';
import styles from './FileModal.module.css';
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { useToaster } from "./Toaster";
import { selectTeams } from "../redux/slices/teamsSlice";
import { selectUser } from "../redux/slices/userSlice";
import {
  selectFile,
  uploadFiles,
  clearUploadResult,
  clearFileError,
} from "../redux/slices/fileSlice";
import {
  selectAssistants,
  setCreateForm,
  fetchFileOptions,
} from "../redux/slices/assistantsSlice.js";

const ALLOW_EXT = [".pdf", ".txt"];
const MAX_FILE_MB = 100;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

function getExt(name = "") {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export default function FileModal({ modalOpen, onHide }) {
  const dispatch = useDispatch();
  const assistants = useSelector(selectAssistants);
  const teams = useSelector(selectTeams);
  const user = useSelector(selectUser);
  const file = useSelector(selectFile);
  const rootEl = typeof document !== 'undefined' ? document.getElementById('root') : null;
  const [pickedFiles, setPickedFiles] = useState([]);
  const { push } = useToaster();

  // 處理現有檔案的勾選
  const handleChange = (e, file_token) => {
    const currentFiles = assistants?.createForm?.files || [];
    if (e.target.checked) {
      const newFiles = [...new Set([...currentFiles, file_token])];
      dispatch(setCreateForm({
        ...assistants?.createForm,
        files: newFiles,
      }));
    } else {
      const newFiles = currentFiles.filter(t => t !== file_token);
      dispatch(setCreateForm({
        ...assistants?.createForm,
        files: newFiles,
      }));
    }
  }

  // 處理上傳檔案的驗證
  function addFiles(listLike) {
    const incoming = Array.from(listLike || []);
    if (incoming.length === 0) return;

    const ok = [];
    const bad = [];

    for (const f of incoming) {
      const ext = getExt(f.name);
      const extOk = ALLOW_EXT.includes(ext);
      const sizeOk = f.size <= MAX_FILE_BYTES;

      if (!extOk) {
        bad.push(`${f.name}（不支援格式）`);
        continue;
      }
      if (!sizeOk) {
        bad.push(`${f.name}（超過 ${MAX_FILE_MB}MB）`);
        continue;
      }
      ok.push(f);
    }

    if (bad.length) {
      push({
        title: "檔案格式不符",
        message: bad.slice(0, 5).join("、") + (bad.length > 5 ? "…" : ""),
        variant: "warning",
      });
    }

    if (ok.length) {
      setPickedFiles((prev) => {
        const map = new Map(prev.map((f) => [`${f.name}__${f.size}`, f]));
        for (const f of ok) map.set(`${f.name}__${f.size}`, f);
        return Array.from(map.values());
      });
    }
  }

  function onPick(e) {
    addFiles(e.target.files);
    e.target.value = ""; // 重置 input 以便重複選擇同檔名
  }

  function onUpload() {
    const team_token = teams.team_selected;
    const account_token = user.data.token;

    if (!pickedFiles.length) {
      push({ title: "請先選擇檔案", message: "至少要選 1 個檔案才能上傳", variant: "warning" });
      return;
    }

    dispatch(
      uploadFiles({
        team_token,
        account_token,
        files: pickedFiles,
      })
    );
  }

  useEffect(() => {
    const r = file.uploadResult;
    const ok = (Array.isArray(r) && r.length > 0) || (!!r && !Array.isArray(r));

    if (!ok) return;

    push({
      title: "上傳完成",
      message: `已上傳 ${pickedFiles.length || 0} 個檔案`,
      variant: "success",
    });

    setPickedFiles([]);
    dispatch(clearUploadResult());

    // 重新抓取列表
    if ((teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(fetchFileOptions({ team_token: teams.team_selected, account_token: user.data.token }));
    }
  }, [file.uploadResult, push, dispatch, teams?.team_selected, user?.data?.token]);

  useEffect(() => {
    const e = file.error;
    const msg = (e && (e.msg || e.message)) || "";

    if (msg) {
      push({ title: "發生錯誤", message: msg, variant: "danger" });
      dispatch(clearFileError());
    }
  }, [file.error, push, dispatch]);

  // 取得目前已勾選的數量，用於顯示 UI 狀態
  const checkedCount = (assistants?.createForm?.files || []).length;
  const existingFiles = assistants?.fileOptions || [];

  return (
    <Modal
      show={modalOpen}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      className={styles.modalRoot}
      dialogClassName={styles.dialog}
      contentClassName={styles.modalContent}
      container={rootEl}
    >
      {/* 標頭 */}
      <div className={styles.modalHeader}>
        <div className={styles.modalTitle}>
          <i className="bi bi-database-check me-2" style={{ color: '#d84040' }}></i>
          選擇知識庫檔案
        </div>
        <button type="button" className={styles.closeBtnHeader} onClick={onHide} aria-label="Close">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* 內容區：雙欄 */}
      <div className={styles.modalBody}>
        
        {/* 左側：現有列表 */}
        <div className={styles.leftPanel}>
          <div className={styles.sectionTitle}>
            <i className="bi bi-list-columns-reverse"></i>
            現有檔案 ({existingFiles.length})
          </div>

          <div className={styles.scrollArea}>
            {existingFiles.length === 0 && (
              <div className={styles.emptyState}>
                <i className="bi bi-inbox fs-1 mb-2"></i>
                <div>暫無檔案</div>
              </div>
            )}

            {existingFiles.map((item) => (
              <div 
                className={styles.fileItem} 
                key={item.file_token}
                onClick={() => {
                   // 點擊整行也能觸發 checkbox
                   document.getElementById(`check-${item.file_token}`).click();
                }}
              >
                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`check-${item.file_token}`}
                    value={item.file_token}
                    checked={(assistants?.createForm?.files || []).includes(item.file_token)}
                    onChange={(e) => handleChange(e, item.file_token)}
                    onClick={(e) => e.stopPropagation()} // 避免冒泡
                  />
                </div>
                <label 
                  className={styles.fileLabel} 
                  htmlFor={`check-${item.file_token}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {`${item.file_name}${item.ext}`}
                </label>
                <i className="bi bi-file-earmark-text text-secondary ms-2"></i>
              </div>
            ))}
          </div>
          
          <div className="mt-3 text-secondary small">
            已選擇 <strong className="text-danger">{checkedCount}</strong> 個檔案作為背景知識。
          </div>
        </div>

        {/* 右側：上傳區 */}
        <div className={styles.rightPanel}>
          <div className={styles.sectionTitle}>
            <i className="bi bi-cloud-arrow-up"></i>
            上傳新檔
          </div>

          <div className={styles.uploadZone}>
            <input
              className={styles.uploadInput}
              multiple
              type="file"
              accept={ALLOW_EXT.join(",")}
              onChange={onPick}
              title="點擊或拖曳檔案至此"
            />
            <i className={`bi bi-cloud-plus ${styles.uploadIcon}`}></i>
            <div className={styles.uploadHint}>
              點擊選擇檔案
            </div>
            <div style={{fontSize: '0.7rem', color:'#999', marginTop: 4}}>
              PDF / TXT (Max {MAX_FILE_MB}MB)
            </div>
          </div>

          <div className={styles.pickedList}>
            {pickedFiles.length === 0 && (
              <div className="text-center text-muted small mt-4">
                尚未選擇上傳檔案
              </div>
            )}
            {pickedFiles.map((f, i) => (
              <div key={`${f.name}-${i}`} className={styles.pickedItem}>
                <span className={styles.pickedName} title={f.name}>{f.name}</span>
                <i
                  className={`bi bi-x-circle-fill text-danger ${styles.pickedRemove}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setPickedFiles(prev => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            className={`${styles.btnAction} ${styles.btnUpload}`}
            onClick={onUpload}
            disabled={pickedFiles.length === 0}
          >
            {file.loading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-send-check"></i>
            )}
            確認上傳
          </button>
        </div>

      </div>

      {/* 底部 */}
      <div className={styles.modalFooter}>
        <button type="button" className={styles.btnClose} onClick={onHide}>
          完成並關閉
        </button>
      </div>
    </Modal>
  );
}